// This is a part of the Cadcorp SIS source code. 
// Copyright (c) NEC Software Solutions UK Limited 1991-2026 
import Vue from 'Vue';
import Vuex, { StoreOptions } from 'Vuex';

// Store modules
import mapState from '../ol/mapState';
import extensionState from './extensionState'
import profileState from './profileState';
import bookmarkState from './bookmarkstate';

// Abstractions
import { IRefreshToken, IRootState, ISupportedCulture, IUrlParameters } from '../../../../CommonScripts/Abstractions';

// Wrappers
import geognosisTileImage from '../ol/source/GeognoSISTileImage';
import { AjaxWrapper } from '../Core/AjaxWrapper';
import { LayerStatus } from '../GeognoSIS/Overlay';
import { uiState } from '../Components/MapView/uiState';

declare const ol: any;

Vue.use(Vuex);
const store: StoreOptions<IRootState> = {
    state: {
        ajaxDefaults: {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': null
            }
        },
        baseUrl: null,
        mapName: null,
        localStorageName: null,
        loadedLanguages: {},
        supportedCultures: null,
        currentCulture: null,
        refreshInterval: null,
        token: null,
        userHelpUrl: null,
        useDarkText: false,
        showHumanDialog: false,
        initialRoute: null,
        urlParameters: {},
        cookiePolycyUrl: null
    },
    getters: {
        baseUrl: state => {
            return state.baseUrl;
        },
        currentCulture: state => {
            return state.currentCulture;
        },
        getUserHelpUrl: state => {
            return state.userHelpUrl;
        },
        useDarkText: state => {
            return state.useDarkText;
        },
        textCssClass: (state) => {
            return state.useDarkText ? 'black--text' : 'white--text';
        },
        getShowHumanDialog: state => {
            return state.showHumanDialog;
        },
        getInitialRoute: (state) => {
            return state.initialRoute;
        },
        getUrlParameters: (state) => {
            return state.urlParameters;
        },
        getCookiePolicyUrl: (state) => {
            return state.cookiePolycyUrl;
        },
        getTokenId: state => {
            return state.token.id;
        },
        getAuthorisation: state => {
            let auth = null;
            if (state?.ajaxDefaults?.headers['Authorization']) {
                auth = state.ajaxDefaults.headers['Authorization'];
                if (auth)
                    auth = auth.substring(auth.indexOf(' ') + 1);
            }
            return auth;
        }
    },
    mutations: {
        setBaseUrl(state, baseUrl) {
            state.baseUrl = baseUrl;
        },
        setAjaxDefaults(state, ajaxDefaults) {
            state.ajaxDefaults = ajaxDefaults;
        },
        setMapName(state, mapName) {
            state.mapName = mapName;
        },
        setLocalStorageName(state, localStorageName) {
            state.localStorageName = localStorageName;
        },
        addLanguage(state, config) {
            if (!state.loadedLanguages[config.culture])
                Vue.set(state.loadedLanguages, config.culture, config.language);
        },
        setSupportedCultures(state, supportedCultures: ISupportedCulture[]) {
            state.supportedCultures = supportedCultures;
        },
        setCurrentCulture(state, culture: string) {
            state.currentCulture = culture;
        },
        setRefreshInterval(state, newInterval: number) {
            state.refreshInterval = newInterval;
        },
        setRefreshToken(state, newToken: IRefreshToken) {
            state.token = newToken;
        },
        setAuthorisation(state, newToken) {
            state.ajaxDefaults.headers['Authorization'] = `Bearer ${newToken}`;
        },
        setUserHelpUrl(state, url: string) {
            state.userHelpUrl = url;
        },
        setUseDarkText(state, value: boolean) {
            state.useDarkText = value;
        },
        setShowHumanDialog(state, value: boolean): void {
            state.showHumanDialog = value;
        },
        setInitialRoute(state, value: string): void {
            state.initialRoute = value;
        },
        setUrlParameters(state, value: IUrlParameters): void {
            state.urlParameters = value;
        },
        setCookiePolycyUrl(state, value: string): void {
            state.cookiePolycyUrl = value;
        }
    },
    actions: {
        loadLanguage(context, payload:{culture: string}) {
            const url = `${context.rootState.baseUrl}${payload.culture}`;
            const ajax = new AjaxWrapper(context.rootState.ajaxDefaults);
            return ajax.get(url, null)
                .then((response: {data: any}) => {
                    const culture: {culture: string, language: any} = {
                        culture: payload.culture,
                        language: response.data
                    };
                    context.commit('addLanguage', culture);
                    return culture.language;
                });
        }
    },
    modules: {
        map: mapState,
        ui: uiState,
        extensions: extensionState,
        bookmarks: bookmarkState,
        profile: profileState
    }
}

export default new Vuex.Store<IRootState>(store);

